"use client";

// Penampil Popup storefront (mig 194). Ter-mount global via Providers; no-op
// di /admin (resolvePopupPageKey → null). Ambil popup aktif sekali, lalu tiap
// pindah halaman pilih SATU popup yang cocok (halaman + audiens + frekuensi)
// via helper murni src/lib/popups.ts. Penanda "sudah tampil" disimpan di
// sessionStorage (sekali per sesi) + localStorage (sekali per hari).
//
// PopupCard diekspor supaya pratinjau live di editor admin (AdminPopups)
// merender persis seperti storefront.

import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { X } from "lucide-react";
import { useCustomer } from "@/context/CustomerContext";
import { fetchActivePopups } from "@/lib/supabase/popups";
import {
  countdownParts,
  formatCountdown,
  isInternalPopupLink,
  pickPopup,
  popupStorageKey,
  POPUP_TEXT_SIZE_PX,
  resolvePopupPageKey,
  shouldSuppressPopup,
  type Popup,
  type PopupBlock,
  type PopupCountdownBlock,
} from "@/lib/popups";
import { StoreImage } from "@/components/StoreImage";

function isSuppressed(popup: Popup): boolean {
  try {
    const key = popupStorageKey(popup.id);
    return shouldSuppressPopup(popup, {
      sessionShown: sessionStorage.getItem(key) === "1",
      lastShownAt: localStorage.getItem(key),
    });
  } catch {
    return false; // storage tak tersedia (private mode) → tampilkan saja
  }
}

function markShown(popup: Popup) {
  try {
    const key = popupStorageKey(popup.id);
    sessionStorage.setItem(key, "1");
    localStorage.setItem(key, new Date().toISOString());
  } catch {
    /* abaikan */
  }
}

/** Hitung mundur live (tick per detik). */
function CountdownView({ block }: { block: PopupCountdownBlock }) {
  const [parts, setParts] = useState(() => countdownParts(block.target));

  useEffect(() => {
    setParts(countdownParts(block.target));
    const id = setInterval(
      () => setParts(countdownParts(block.target)),
      1000
    );
    return () => clearInterval(id);
  }, [block.target]);

  return (
    <div style={{ textAlign: block.align, color: block.color }}>
      {block.label ? (
        <p className="text-xs font-bold opacity-80">{block.label}</p>
      ) : null}
      <p className="text-lg font-extrabold tabular-nums">
        {parts.expired ? block.expiredText : formatCountdown(parts)}
      </p>
    </div>
  );
}

/** Kartu isi popup — dipakai storefront & pratinjau editor admin. */
export function PopupCard({
  imageUrl,
  imageLink,
  blocks,
  onNavigate,
}: {
  imageUrl: string | null;
  imageLink?: string | null;
  blocks: PopupBlock[];
  /** Dipanggil saat link internal (tombol/gambar) diklik (storefront: push). */
  onNavigate?: (url: string) => void;
}) {
  // PF-09: width=0/height=0 + h-auto = pola next/image utk rasio intrinsik tak
  // diketahui; default StoreImage (priority=false) sudah memberi loading lazy.
  const image = imageUrl ? (
    <StoreImage
      src={imageUrl}
      alt=""
      width={0}
      height={0}
      sizes="(max-width: 448px) 100vw, 384px"
      className="block h-auto max-h-96 w-full object-contain"
    />
  ) : null;

  return (
    <div className="w-full overflow-hidden rounded-2xl bg-white shadow-2xl">
      {image && imageLink ? (
        isInternalPopupLink(imageLink) ? (
          <button
            type="button"
            onClick={() => onNavigate?.(imageLink.trim())}
            className="block w-full"
          >
            {image}
          </button>
        ) : (
          <a
            href={imageLink.trim()}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            {image}
          </a>
        )
      ) : (
        image
      )}
      {blocks.length > 0 ? (
        <div className="space-y-3 p-4">
          {blocks.map((b, i) =>
            b.type === "text" ? (
              <p
                key={i}
                className="whitespace-pre-line leading-snug"
                style={{
                  fontSize: POPUP_TEXT_SIZE_PX[b.size],
                  textAlign: b.align,
                  color: b.color,
                  fontWeight: b.bold ? 800 : 500,
                }}
              >
                {b.text}
              </p>
            ) : b.type === "countdown" ? (
              <CountdownView key={i} block={b} />
            ) : (
              <div key={i} style={{ textAlign: b.align }}>
                {isInternalPopupLink(b.url) ? (
                  <button
                    type="button"
                    onClick={() => onNavigate?.(b.url.trim())}
                    className="inline-block rounded-lg px-5 py-2.5 text-sm font-extrabold"
                    style={{ backgroundColor: b.color, color: b.textColor }}
                  >
                    {b.label}
                  </button>
                ) : (
                  <a
                    href={b.url.trim()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block rounded-lg px-5 py-2.5 text-sm font-extrabold"
                    style={{ backgroundColor: b.color, color: b.textColor }}
                  >
                    {b.label}
                  </a>
                )}
              </div>
            )
          )}
        </div>
      ) : null}
    </div>
  );
}

export function PopupDisplay() {
  const pathname = usePathname();
  const router = useRouter();
  const { customer, isLoading, isAuthenticated } = useCustomer();

  // null = belum pernah fetch; [] = sudah fetch (mungkin kosong).
  const [popups, setPopups] = useState<Popup[] | null>(null);
  const [active, setActive] = useState<Popup | null>(null);
  // Path terakhir yang sudah dievaluasi — supaya popup tak dievaluasi ulang
  // (dan hilang sendiri) saat context customer berubah di halaman yang sama.
  const [evaluatedPath, setEvaluatedPath] = useState<string | null>(null);

  const pageKey = resolvePopupPageKey(pathname ?? "/");

  // Ambil popup aktif — lazy, hanya saat berada di halaman storefront target.
  useEffect(() => {
    if (popups !== null || !pageKey) return;
    let cancelled = false;
    fetchActivePopups()
      .then((rows) => {
        if (!cancelled) setPopups(rows);
      })
      .catch(() => {
        if (!cancelled) setPopups([]); // gagal fetch → tanpa popup, jangan ganggu toko
      });
    return () => {
      cancelled = true;
    };
  }, [pageKey, popups]);

  // Evaluasi popup tiap pindah halaman (setelah data & status login siap).
  // Menghormati delay tampil (delaySeconds) via timer yang dibersihkan saat
  // pindah halaman/unmount.
  useEffect(() => {
    if (!pathname || pathname === evaluatedPath) return;
    if (isLoading || popups === null) return; // tunggu context + data
    if (!pageKey) {
      setEvaluatedPath(pathname);
      setActive(null);
      return;
    }

    const picked = pickPopup(
      popups,
      {
        pageKey,
        isLoggedIn: isAuthenticated,
        groupId: customer?.group?.id ?? null,
      },
      isSuppressed
    );
    setEvaluatedPath(pathname);
    setActive(null);

    if (!picked) return;

    const show = () => {
      markShown(picked);
      setActive(picked);
    };
    if (picked.delaySeconds > 0) {
      const id = setTimeout(show, picked.delaySeconds * 1000);
      return () => clearTimeout(id);
    }
    show();
  }, [
    pathname,
    evaluatedPath,
    pageKey,
    popups,
    isLoading,
    isAuthenticated,
    customer?.group?.id,
  ]);

  if (!active) return null;

  const close = () => setActive(null);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-6"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label={active.name}
    >
      <div
        className="relative w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <PopupCard
          imageUrl={active.imageUrl}
          imageLink={active.imageLink}
          blocks={active.blocks}
          onNavigate={(url) => {
            close();
            router.push(url);
          }}
        />
        <button
          type="button"
          onClick={close}
          aria-label="Tutup popup"
          className="absolute -top-3 -right-3 grid h-8 w-8 place-items-center rounded-full bg-white text-neutral-600 shadow-lg"
        >
          <X size={17} />
        </button>
      </div>
    </div>
  );
}
